import { computeEditableVisibility } from '../editableVisibility';
import type { GsapTimelineLike } from '../editableVisibility';
import type { LintFinding, LintSeverity } from './types';

/**
 * The loaded composition, for rules that need more than the HTML source — the live DOM and the
 * seekable timeline. `timeline` is null when the composition declares no `window.__timelines.main`.
 */
export interface RuntimeLintContext {
    document: Document;
    timeline: GsapTimelineLike | null;
}

/**
 * The runtime counterpart to the static `LintRule`: it inspects the loaded composition rather than
 * the raw HTML string. The asset runs these after loading the iframe and merges their findings with
 * the static ones into one `LintFinding[]`.
 */
export interface RuntimeLintRule {
    id: string;
    severity: LintSeverity;
    guidance?: string;
    check(context: RuntimeLintContext): string[];
}

/**
 * Natural text editing shows one click-to-edit frame with every editable field's overlay at once,
 * so all `[data-l5-editable]` nodes must be visible together at some single frame. The author fixes
 * a violation by adjusting the animation, not by adding markup.
 */
const editableCoVisibility: RuntimeLintRule = {
    id: 'editable-co-visibility',
    severity: 'error',
    guidance:
        'All `data-l5-editable` elements must be simultaneously visible at some single frame — the editor reveals them together under one click-to-edit overlay. They may animate in and out, but there must be a moment where every one is on screen.',
    check(context: RuntimeLintContext): string[] {
        if (!context.timeline) {
            return [];
        }
        const visibility = computeEditableVisibility(context.document, context.timeline);
        if (visibility.hasEditableNodes && visibility.sharedWindow === null) {
            return [
                'Editable text nodes (`data-l5-editable`) are never all visible at the same frame. Adjust the animation so they share a visible window.',
            ];
        }
        return [];
    },
};

export const CORE_RUNTIME_LINT_RULES: RuntimeLintRule[] = [editableCoVisibility];

/**
 * Run the runtime rules against a loaded composition. Synchronous (the composition is already
 * loaded); the asset awaits the load, then calls this.
 */
export function runRuntimeLintRules(
    context: RuntimeLintContext,
    rules: RuntimeLintRule[] = CORE_RUNTIME_LINT_RULES,
): LintFinding[] {
    const findings: LintFinding[] = [];
    for (const rule of rules) {
        for (const message of rule.check(context)) {
            findings.push({ ruleId: rule.id, severity: rule.severity, message });
        }
    }
    return findings;
}
